import { Link } from "@mui/material";
import { TFunction } from "i18next";
import RichText from "components/basic/richText";
import Typography from "components/basic/typography";
import SubProcessorTable from "./components/SubProcessorTable";
import { PLACEHOLDER } from "./constants";

/**
 * The policy text.
 *
 * Every factual claim here is derived from what the code actually does — the
 * fields on Account and Vendor, the S3 upload prefixes, the third-party calls
 * in ev-server, the cookies set by index.ts, and the retention windows in
 * pruneAnalyticsEvents.ts and privatePortal/tokens.ts. If any of those change,
 * this file is part of the change.
 */
export interface PolicySection {
  id: string;
  title: string;
  body: React.ReactNode;
}

const P: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <Typography variant="body1" sx={{ marginBottom: 2 }}>
    {children}
  </Typography>
);

const Bullets: React.FC<{ items: React.ReactNode[] }> = ({ items }) => (
  <Typography
    variant="body1"
    component="li"
    sx={{ marginBottom: 2, paddingLeft: 0 }}
  >
    <ul style={{ margin: 0, paddingLeft: "1.25rem" }}>
      {items.map((item, index) => (
        <li key={index} style={{ marginBottom: "0.5rem" }}>
          {item}
        </li>
      ))}
    </ul>
  </Typography>
);

export const LAST_UPDATED = "2026-09-23";

export const policySections = (t: TFunction<"legal">): PolicySection[] => [
  {
    id: "who-we-are",
    title: t("privacy.who_we_are.title"),
    body: (
      <>
        <P>{t("privacy.who_we_are.p1", PLACEHOLDER)}</P>
        <P>{t("privacy.who_we_are.p2")}</P>
        <P>
          <RichText
            t={t}
            i18nKey="privacy.who_we_are.p3"
            values={PLACEHOLDER}
            components={{
              link1: <Link href={`mailto:${PLACEHOLDER.email}`} />,
            }}
          />
        </P>
      </>
    ),
  },
  {
    id: "what-we-collect",
    title: t("privacy.what_we_collect.title"),
    body: (
      <>
        <P>
          <RichText
            t={t}
            i18nKey="privacy.what_we_collect.p1"
            components={{ strong: <strong /> }}
          />
        </P>
        <P>
          <RichText
            t={t}
            i18nKey="privacy.what_we_collect.p2"
            components={{ strong: <strong /> }}
          />
        </P>
        <P>
          <RichText
            t={t}
            i18nKey="privacy.what_we_collect.p3"
            components={{ strong: <strong /> }}
          />
        </P>
        <P>
          <RichText
            t={t}
            i18nKey="privacy.what_we_collect.p4"
            components={{ strong: <strong /> }}
          />
        </P>
        <P>
          <RichText
            t={t}
            i18nKey="privacy.what_we_collect.p5"
            components={{ strong: <strong /> }}
          />
        </P>
      </>
    ),
  },
  {
    id: "how-we-use-it",
    title: t("privacy.how_we_use_it.title"),
    body: (
      <>
        <Bullets
          items={[
            <RichText
              t={t}
              i18nKey="privacy.how_we_use_it.b1"
              components={{ strong: <strong /> }}
            />,
            <RichText
              t={t}
              i18nKey="privacy.how_we_use_it.b2"
              components={{ strong: <strong /> }}
            />,
            <RichText
              t={t}
              i18nKey="privacy.how_we_use_it.b3"
              components={{ strong: <strong /> }}
            />,
            <RichText
              t={t}
              i18nKey="privacy.how_we_use_it.b4"
              components={{ strong: <strong /> }}
            />,
            <RichText
              t={t}
              i18nKey="privacy.how_we_use_it.b5"
              components={{ strong: <strong /> }}
            />,
            <RichText
              t={t}
              i18nKey="privacy.how_we_use_it.b6"
              components={{ strong: <strong /> }}
            />,
          ]}
        />
        <P>
          <RichText
            t={t}
            i18nKey="privacy.how_we_use_it.p1"
            components={{ strong: <strong /> }}
          />
        </P>
        <P>{t("privacy.how_we_use_it.p2")}</P>
      </>
    ),
  },
  {
    id: "who-we-share-with",
    title: t("privacy.who_we_share_with.title"),
    body: (
      <>
        <P>
          <RichText
            t={t}
            i18nKey="privacy.who_we_share_with.p1"
            components={{ strong: <strong /> }}
          />
        </P>
        <P>
          <RichText
            t={t}
            i18nKey="privacy.who_we_share_with.p2"
            components={{ strong: <strong /> }}
          />
        </P>
        <SubProcessorTable />
        <P>
          <RichText
            t={t}
            i18nKey="privacy.who_we_share_with.p3"
            components={{ strong: <strong /> }}
          />
        </P>
      </>
    ),
  },
  {
    id: "cookies-and-analytics",
    title: t("privacy.cookies_and_analytics.title"),
    body: (
      <>
        <P>
          <RichText
            t={t}
            i18nKey="privacy.cookies_and_analytics.p1"
            components={{ strong: <strong />, code: <code /> }}
          />
        </P>
        <P>
          <RichText
            t={t}
            i18nKey="privacy.cookies_and_analytics.p2"
            components={{ strong: <strong /> }}
          />
        </P>
        <P>{t("privacy.cookies_and_analytics.p3")}</P>
        <P>
          <RichText
            t={t}
            i18nKey="privacy.cookies_and_analytics.p4"
            components={{ strong: <strong /> }}
          />
        </P>
        <P>
          <RichText
            t={t}
            i18nKey="privacy.cookies_and_analytics.p5"
            components={{
              link1: (
                <Link
                  href="https://www.hotjar.com/policies/do-not-track/"
                  target="_blank"
                  rel="noopener noreferrer"
                />
              ),
              strong: <strong />,
            }}
          />
        </P>
        <P>
          <RichText
            t={t}
            i18nKey="privacy.cookies_and_analytics.p6"
            components={{ strong: <strong /> }}
          />
        </P>
      </>
    ),
  },
  {
    id: "how-long",
    title: t("privacy.how_long.title"),
    body: (
      <Bullets
        items={[
          <RichText
            t={t}
            i18nKey="privacy.how_long.b1"
            components={{ strong: <strong /> }}
          />,
          <RichText
            t={t}
            i18nKey="privacy.how_long.b2"
            components={{ strong: <strong /> }}
          />,
          <RichText
            t={t}
            i18nKey="privacy.how_long.b3"
            components={{ strong: <strong /> }}
          />,
          <RichText
            t={t}
            i18nKey="privacy.how_long.b4"
            components={{ strong: <strong /> }}
          />,
          <RichText
            t={t}
            i18nKey="privacy.how_long.b5"
            components={{ strong: <strong /> }}
          />,
        ]}
      />
    ),
  },
  {
    id: "your-rights",
    title: t("privacy.your_rights.title"),
    body: (
      <>
        <P>{t("privacy.your_rights.p1")}</P>
        <Bullets
          items={[
            <RichText
              t={t}
              i18nKey="privacy.your_rights.b1"
              components={{ strong: <strong /> }}
            />,
            <RichText
              t={t}
              i18nKey="privacy.your_rights.b2"
              components={{ strong: <strong /> }}
            />,
            <RichText
              t={t}
              i18nKey="privacy.your_rights.b3"
              components={{ strong: <strong /> }}
            />,
            <RichText
              t={t}
              i18nKey="privacy.your_rights.b4"
              values={PLACEHOLDER}
              components={{
                link1: <Link href={`mailto:${PLACEHOLDER.email}`} />,
                strong: <strong />,
              }}
            />,
          ]}
        />
        <P>{t("privacy.your_rights.p2")}</P>
        <P>
          <RichText
            t={t}
            i18nKey="privacy.your_rights.p3"
            components={{
              link1: (
                <Link
                  href="https://www.priv.gc.ca"
                  target="_blank"
                  rel="noopener noreferrer"
                />
              ),
            }}
          />
        </P>
      </>
    ),
  },
  {
    id: "security",
    title: t("privacy.security.title"),
    body: (
      <>
        <P>{t("privacy.security.p1")}</P>
        <P>{t("privacy.security.p2")}</P>
      </>
    ),
  },
  {
    id: "transfers",
    title: t("privacy.transfers.title"),
    body: <P>{t("privacy.transfers.p1")}</P>,
  },
  {
    id: "children",
    title: t("privacy.children.title"),
    body: <P>{t("privacy.children.p1")}</P>,
  },
  {
    id: "changes",
    title: t("privacy.changes.title"),
    body: <P>{t("privacy.changes.p1")}</P>,
  },
];
